import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Upload, CheckCircle2, FileSpreadsheet, Database, ArrowRight, RotateCcw, History } from 'lucide-react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useApi } from '@/context/AuthContext';

const ENTITIES = [
  { key: 'customers', label: 'Customers', cols: 'name, email, phone, gstin, address, state_code, opening_balance' },
  { key: 'vendors', label: 'Vendors', cols: 'name, email, phone, gstin, address, state_code, opening_balance' },
  { key: 'invoices', label: 'Invoices', cols: 'invoice_number, customer_name, invoice_date, due_date, item_description, quantity, rate, gst_rate, total_amount' },
  { key: 'expenses', label: 'Expenses', cols: 'date, vendor_name, description, category, amount, gst_rate, tax_amount' },
];

const SOURCES = [
  { id: 'tally', name: 'Tally ERP', hint: 'Export ledgers & vouchers to Excel, save as CSV' },
  { id: 'vyapar', name: 'Vyapar', hint: 'Reports → Export to Excel, save as CSV' },
  { id: 'zoho', name: 'Zoho Books', hint: 'Settings → Export data as CSV' },
  { id: 'excel', name: 'Excel / CSV', hint: 'Use our column templates below' },
];

const parseCSV = (text) => {
  const rows = text.trim().split(/\r?\n/).map((line) => {
    const out = []; let cur = ''; let q = false;
    for (const ch of line) {
      if (ch === '"') q = !q;
      else if (ch === ',' && !q) { out.push(cur.trim()); cur = ''; }
      else cur += ch;
    }
    out.push(cur.trim());
    return out;
  });
  const headers = rows.shift().map((h) => h.toLowerCase().replace(/\s+/g, '_'));
  return rows.filter((r) => r.some(Boolean)).map((r) => Object.fromEntries(headers.map((h, i) => [h, r[i] || ''])));
};

const num = (v) => parseFloat(String(v || '0').replace(/[₹,]/g, '')) || 0;
const orNull = (v) => v || null;

const toPayload = (source, raw) => ({
  source,
  customers: (raw.customers || []).filter((c) => c.name).map((c) => ({ name: c.name, email: orNull(c.email), phone: orNull(c.phone), gstin: orNull(c.gstin), address: orNull(c.address), state_code: orNull(c.state_code), opening_balance: num(c.opening_balance) })),
  vendors: (raw.vendors || []).filter((c) => c.name).map((c) => ({ name: c.name, email: orNull(c.email), phone: orNull(c.phone), gstin: orNull(c.gstin), address: orNull(c.address), state_code: orNull(c.state_code), opening_balance: num(c.opening_balance) })),
  invoices: (raw.invoices || []).filter((i) => i.invoice_number && i.customer_name).map((i) => {
    const total = num(i.total_amount) || num(i.quantity || 1) * num(i.rate) * (1 + num(i.gst_rate) / 100);
    const tax = total - total / (1 + num(i.gst_rate) / 100);
    return {
      invoice_number: i.invoice_number, customer_name: i.customer_name, invoice_date: i.invoice_date, due_date: orNull(i.due_date),
      items: [{ description: i.item_description, quantity: num(i.quantity || 1), rate: num(i.rate), gst_rate: num(i.gst_rate) }],
      total_amount: Math.round(total * 100) / 100, tax_amount: Math.round(tax * 100) / 100,
    };
  }),
  expenses: (raw.expenses || []).filter((e) => e.amount).map((e) => ({ ...e, amount: num(e.amount), gst_rate: num(e.gst_rate), tax_amount: num(e.tax_amount) })),
});

const Steps = ({ step }) => (
  <div className="flex items-center gap-3 text-sm">
    {['Source', 'Upload', 'Review', 'Done'].map((s, i) => (
      <React.Fragment key={s}>
        <div className={`flex items-center gap-2 ${i <= step ? 'text-[#1D9E75] font-semibold' : 'text-stone-400'}`}>
          <span className={`w-7 h-7 rounded-full flex items-center justify-center border ${i <= step ? 'bg-[#1D9E75] text-white border-[#1D9E75]' : 'border-stone-300'}`}>{i + 1}</span>{s}
        </div>
        {i < 3 && <div className="w-8 h-px bg-stone-300" />}
      </React.Fragment>
    ))}
  </div>
);

const SourceStep = ({ onPick }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {SOURCES.map((s) => (
      <button key={s.id} onClick={() => onPick(s.id)} data-testid={`migrate-source-${s.id}`}
        className="text-left p-5 rounded-xl border border-stone-200 bg-white hover:border-[#1D9E75] hover:-translate-y-0.5 transition-[transform,border-color]">
        <Database className="w-6 h-6 text-[#1D9E75] mb-3" />
        <p className="font-semibold text-stone-900">{s.name}</p>
        <p className="text-sm text-stone-500">{s.hint}</p>
      </button>
    ))}
  </div>
);

const UploadStep = ({ raw, onFile }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {ENTITIES.map((e) => (
      <label key={e.key} className="block p-5 rounded-xl border-2 border-dashed border-stone-300 bg-white hover:border-[#1D9E75] cursor-pointer transition-colors">
        <input type="file" accept=".csv,text/csv" className="hidden" data-testid={`migrate-upload-${e.key}`}
          onChange={(ev) => ev.target.files[0] && onFile(e.key, ev.target.files[0])} />
        <div className="flex items-center justify-between mb-2">
          <span className="font-semibold text-stone-900 flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-[#EF9F27]" />{e.label}</span>
          {raw[e.key] ? <span className="text-sm text-[#1D9E75] flex items-center gap-1" data-testid={`migrate-count-${e.key}`}><CheckCircle2 className="w-4 h-4" />{raw[e.key].length} rows</span>
            : <Upload className="w-4 h-4 text-stone-400" />}
        </div>
        <p className="text-xs text-stone-500 font-mono">{e.cols}</p>
      </label>
    ))}
  </div>
);

const ReviewStep = ({ payload }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="migrate-review">
    {ENTITIES.map((e) => (
      <div key={e.key} className="p-5 rounded-xl bg-white border border-stone-200">
        <p className="text-sm text-stone-500">{e.label}</p>
        <p className="text-3xl font-bold font-mono text-stone-900" data-testid={`migrate-review-${e.key}`}>{payload[e.key].length}</p>
      </div>
    ))}
  </div>
);

const ResultStep = ({ result, onReset }) => {
  const skipped = (result.errors || []).filter((e) => e.includes('already exists'));
  const failed = (result.errors || []).filter((e) => !e.includes('already exists'));
  const imported = result.customers_imported + result.vendors_imported + result.invoices_imported + result.expenses_imported;
  const title = imported === 0 && failed.length === 0 ? 'Nothing new to import' : failed.length ? 'Migration completed with errors' : 'Migration completed';
  return (
  <div className="p-6 rounded-xl bg-[#1D9E75]/10 border border-[#1D9E75]/30 space-y-4" data-testid="migrate-result">
    <p className="text-lg font-semibold text-stone-900 flex items-center gap-2" data-testid="migrate-result-title"><CheckCircle2 className="w-6 h-6 text-[#1D9E75]" />{title}</p>
    <p className="text-stone-700">
      Imported {result.customers_imported} customers, {result.vendors_imported} vendors, {result.invoices_imported} invoices and {result.expenses_imported} expenses.
      {skipped.length > 0 && ` Skipped ${skipped.length} duplicate record${skipped.length > 1 ? 's' : ''} already in Laksh.`}
    </p>
    {failed.length > 0 && <ul className="text-sm text-red-600 list-disc pl-5">{failed.slice(0, 5).map((e) => <li key={e}>{e}</li>)}</ul>}
    <div className="flex gap-3">
      <Link to="/invoices"><Button className="bg-[#1D9E75] hover:bg-[#1D9E75]/90 text-white" data-testid="migrate-view-invoices">View invoices</Button></Link>
      <Button variant="outline" onClick={onReset} data-testid="migrate-again-btn" className="bg-white border-stone-300 text-stone-800 hover:bg-stone-50"><RotateCcw className="w-4 h-4 mr-2" />Import more</Button>
    </div>
  </div>
  );
};

const HistoryCard = ({ items }) => (
  <Card className="bg-white border-stone-200 text-stone-900">
    <CardHeader><CardTitle className="text-lg text-stone-900 flex items-center gap-2"><History className="w-5 h-5 text-[#EF9F27]" />Migration history</CardTitle></CardHeader>
    <CardContent className="space-y-2" data-testid="migrate-history">
      {items.length === 0 && <p className="text-sm text-stone-500">No migrations yet.</p>}
      {items.map((m) => (
        <div key={m.id} className="flex justify-between text-sm border-b border-stone-100 pb-2">
          <span className="text-stone-700">{new Date(m.created_at).toLocaleString('en-IN')} · <span className="uppercase">{m.source}</span></span>
          <span className="text-stone-500">{m.customers_imported}C · {m.vendors_imported}V · {m.invoices_imported}I · {m.expenses_imported}E</span>
        </div>
      ))}
    </CardContent>
  </Card>
);

export default function MigrationWizardPage() {
  const api = useApi();
  const [step, setStep] = useState(0);
  const [source, setSource] = useState(null);
  const [raw, setRaw] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(() => {
    api.get('/migrate/history').then((r) => setHistory(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const onFile = async (key, file) => {
    try {
      const rows = parseCSV(await file.text());
      setRaw((p) => ({ ...p, [key]: rows }));
      toast.success(`${rows.length} ${key} rows read`);
    } catch {
      toast.error('Could not read this CSV file');
    }
  };

  const payload = toPayload(source, raw);
  const total = ENTITIES.reduce((s, e) => s + payload[e.key].length, 0);

  const runImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/migrate/import', payload);
      setResult(data);
      setStep(3);
      loadHistory();
      const imported = data.customers_imported + data.vendors_imported + data.invoices_imported + data.expenses_imported;
      toast.success(imported ? 'Migration complete' : 'Nothing new to import');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Import failed - check your CSV columns');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setStep(0); setSource(null); setRaw({}); setResult(null); };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="migration-wizard-page">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 font-['Outfit']">Migration Agent</h1>
          <p className="text-stone-600">Move your books from Tally, Vyapar, Zoho or Excel into Laksh in minutes</p>
        </div>
        <Steps step={step} />
        {step === 0 && <SourceStep onPick={(s) => { setSource(s); setStep(1); }} />}
        {step === 1 && <UploadStep raw={raw} onFile={onFile} />}
        {step === 2 && <ReviewStep payload={payload} />}
        {step === 3 && result && <ResultStep result={result} onReset={reset} />}
        {(step === 1 || step === 2) && (
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="migrate-back-btn" className="bg-white border-stone-300 text-stone-800 hover:bg-stone-50">Back</Button>
            {step === 1
              ? <Button disabled={!total} onClick={() => setStep(2)} className="bg-[#1D9E75] hover:bg-[#1D9E75]/90 text-white" data-testid="migrate-review-btn">Review {total} records <ArrowRight className="w-4 h-4 ml-2" /></Button>
              : <Button disabled={busy} onClick={runImport} className="bg-[#EF9F27] hover:bg-[#EF9F27]/90 text-[#06100A] font-semibold" data-testid="migrate-import-btn">{busy ? 'Importing...' : 'Start import'}</Button>}
          </div>
        )}
        <HistoryCard items={history} />
      </div>
    </DashboardLayout>
  );
}
